/**
 * « Modifier le texte » d'une ligne de « Ta voix » : l'accroche de la vidéo
 * (texte, mot en or, ce que dit la voix) ou la narration d'une scène
 * (sous-titres, ce que dit la voix). Le brouillon est revalidé avant
 * d'enregistrer ; si ce que dit la voix change, la voix enregistrée est retirée
 * (après confirmation). Logique : `@/lib/animations/narration-edit`.
 */

import { useId, useState } from 'react';
import { JRAF_LIMITS, type JrafDocument, type VoiceSlot } from '@jeraisonne/animation';
import { Button } from '@jeraisonne/ui';
import { checkedDraft } from '@/lib/animations/api';
import { withNarrationText } from '@/lib/animations/narration-edit';

const FIELD =
  'border-ink-200 focus:border-primary-400 w-full rounded-xl border bg-white px-3 py-2 text-sm outline-none';

/** Valeurs actuelles du texte d'une ligne (accroche ou scène). */
export function currentNarration(doc: JrafDocument, slot: VoiceSlot) {
  if (slot.target.kind === 'hook') {
    const hook = doc.bookends?.hook;
    return { text: hook?.text ?? slot.text, say: hook?.say ?? '', emphasis: hook?.emphasis ?? '' };
  }
  const sceneId = slot.target.kind === 'scene' ? slot.target.sceneId : null;
  const scene = doc.scenes.find((s) => s.id === sceneId);
  return {
    text: scene?.narration.text ?? slot.text,
    say: scene?.narration.say ?? '',
    emphasis: '',
  };
}

export function NarrationTextEditor({
  draft,
  doc,
  slot,
  toolIds,
  saving,
  onSave,
  onCancel,
}: {
  draft: unknown;
  doc: JrafDocument;
  slot: VoiceSlot;
  toolIds?: readonly string[];
  saving: boolean;
  onSave: (checked: ReturnType<typeof checkedDraft>) => void;
  onCancel: () => void;
}) {
  const isHook = slot.target.kind === 'hook';
  const initial = currentNarration(doc, slot);
  const [text, setText] = useState(initial.text);
  const [say, setSay] = useState(initial.say);
  const [emphasis, setEmphasis] = useState(initial.emphasis);
  const [error, setError] = useState<string | null>(null);
  const ids = { text: useId(), say: useId(), emphasis: useId() };
  const max = isHook ? JRAF_LIMITS.maxHookChars : JRAF_LIMITS.maxNarrationChars;

  const submit = () => {
    if (text.trim().length < 3) {
      setError('Écris au moins quelques mots.');
      return;
    }
    const { draft: next, voiceRemoved } = withNarrationText(draft, slot.target, {
      text,
      say,
      emphasis,
    });
    const checked = checkedDraft(next, toolIds);
    if (!checked.valid && checkedDraft(draft, toolIds).valid) {
      setError(checked.errors[0]?.message ?? 'Ce texte n’est pas accepté.');
      return;
    }
    if (
      voiceRemoved &&
      !window.confirm('Ta voix enregistrée ne dit plus ce texte : elle sera retirée. Continuer ?')
    )
      return;
    setError(null);
    onSave(checked);
  };

  return (
    <div className="border-ink-200 bg-ink-50 mt-2 space-y-3 rounded-xl border p-3">
      <div>
        <label htmlFor={ids.text} className="text-ink-700 text-[13px] font-semibold">
          {isHook ? 'Texte de l’accroche' : 'Texte (sous-titres)'}
        </label>
        <textarea
          id={ids.text}
          value={text}
          maxLength={max}
          rows={isHook ? 2 : 4}
          onChange={(e) => setText(e.target.value)}
          className={FIELD}
        />
        <p className="text-ink-500 text-right text-[12px]">
          {text.trim().length} / {max}
          {isHook ? ' · une phrase simple, sans formule' : ' · formules entre $…$'}
        </p>
      </div>
      {isHook && (
        <div>
          <label htmlFor={ids.emphasis} className="text-ink-700 text-[13px] font-semibold">
            Mot en or (facultatif)
          </label>
          <input
            id={ids.emphasis}
            value={emphasis}
            maxLength={max}
            onChange={(e) => setEmphasis(e.target.value)}
            placeholder="un mot du texte"
            className={`${FIELD} min-h-11`}
          />
        </div>
      )}
      <div>
        <label htmlFor={ids.say} className="text-ink-700 text-[13px] font-semibold">
          Ce que dit la voix (facultatif)
        </label>
        <textarea
          id={ids.say}
          value={say}
          maxLength={isHook ? max * 2 : max}
          rows={2}
          onChange={(e) => setSay(e.target.value)}
          placeholder="si on ne le lit pas comme c’est écrit (« A quatre », « racine de deux »)"
          className={FIELD}
        />
      </div>
      {error && (
        <p role="alert" className="text-error-600 text-sm">
          {error}
        </p>
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Annuler
        </Button>
        <Button size="sm" onClick={submit} disabled={saving}>
          {saving ? 'Enregistrement…' : 'Enregistrer le texte'}
        </Button>
      </div>
    </div>
  );
}
